import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Upload, Plus } from "lucide-react";

export function SignersDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: "", role: "docente", file: null });
  const { data: signers = [] } = useQuery({ queryKey: ["cert-signers"], queryFn: async () => (await api.get("/cert-gen/signers")).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["cert-signers"] });

  async function add() {
    if (!f.name.trim()) return toast.error("Inserisci il nome");
    const fd = new FormData();
    fd.append("name", f.name); fd.append("role", f.role);
    if (f.file) fd.append("file", f.file);
    try { await api.post("/cert-gen/signers", fd); setF({ name: "", role: f.role, file: null }); refresh(); toast.success("Firmatario aggiunto"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  }
  async function upload(id, file) {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/cert-gen/signers/${id}/signature`, fd); refresh(); toast.success("Firma aggiornata"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  }
  async function del(id) {
    if (!window.confirm("Eliminare questo firmatario?")) return;
    await api.delete(`/cert-gen/signers/${id}`); refresh();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white" data-testid="signers-dialog">
        <DialogHeader><DialogTitle>Firmatari e firme</DialogTitle></DialogHeader>
        <div className="text-[12px] text-stone-500">Per un risultato migliore usa un PNG con sfondo trasparente.</div>
        <ul className="divide-y divide-stone-100 border border-stone-200 rounded-lg">
          {signers.length === 0 && <li className="p-4 text-sm text-stone-400 text-center">Nessun firmatario.</li>}
          {signers.map((s) => (
            <li key={s.id} className="p-3 flex items-center gap-3" data-testid={`signer-${s.id}`}>
              <div className="flex-1">
                <div className="text-sm font-medium">{s.name}</div>
                <div className="text-[11px] text-stone-500 capitalize">{s.role} · {s.signature_path ? "firma caricata" : "senza firma"}</div>
              </div>
              <label className="cursor-pointer text-[11px] inline-flex items-center gap-1 rounded-full border border-stone-300 px-3 h-8 hover:bg-stone-50">
                <Upload size={12}/> Firma
                <input type="file" accept=".png,.jpg,.jpeg" className="hidden" onChange={(e) => e.target.files?.[0] && upload(s.id, e.target.files[0])}/>
              </label>
              <button onClick={() => del(s.id)} className="text-stone-400 hover:text-red-600"><Trash2 size={14}/></button>
            </li>
          ))}
        </ul>
        <div className="grid grid-cols-[1fr_130px] gap-2">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Es. dott. Gennaro Chiocca" data-testid="signer-name"/>
          <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
            <SelectTrigger data-testid="signer-role"><SelectValue/></SelectTrigger>
            <SelectContent><SelectItem value="direttore">Direttore</SelectItem><SelectItem value="docente">Docente</SelectItem></SelectContent>
          </Select>
          <Input type="file" accept=".png,.jpg,.jpeg" onChange={(e) => setF({ ...f, file: e.target.files?.[0] || null })} data-testid="signer-file"/>
          <Button onClick={add} className="bg-[#1C352D] hover:bg-[#264A3E]" data-testid="signer-add"><Plus size={14} className="mr-1"/> Aggiungi</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
